import { useDispatch, useSelector } from "react-redux";
import { useState } from "react";
import { MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import { toast } from "react-toastify";
import Loader from "../../components/Loader";
import {
  fetchStaffLeaderboard,
  fetchReferrerDetail,
  fetchReferralDetail,
  fetchReferralLink,
} from "../../actions/referralActions";
import {
  modalOverlay,
  modalBox,
  modalHeader,
  modalBody,
  closeBtn,
  downloadBtnStyle,
  downloadCSV,
} from "./shared";

const StaffLeaderboard = ({ stateLabels, fromDate, toDate }) => {
  const dispatch = useDispatch();
  const [staffPage, setStaffPage] = useState(1);
  const [copyingTag, setCopyingTag] = useState(null);

  const [referrerModal, setReferrerModal] = useState(false);
  const [selectedReferrer, setSelectedReferrer] = useState(null);
  const [referralModal, setReferralModal] = useState(false);
  const [selectedReferral, setSelectedReferral] = useState(null);

  const { loading: staffLoading, data: staffData, error: staffError } =
    useSelector((state) => state.getStaffLeaderboard) || {};
  const { loading: detailLoading, data: detailData, error: detailError } =
    useSelector((state) => state.getReferrerDetail) || {};
  const { loading: referralDetailLoading, data: referralDetailData, error: referralDetailError } =
    useSelector((state) => state.getReferralDetail) || {};
  const { loading: linkLoading, data: linkData, error: linkError } =
    useSelector((state) => state.getReferralLink) || {};

  const staffLeaderboard = staffData?.data;
  const detail = detailData?.data;
  const referralDetail = referralDetailData?.data;
  const referralLink = linkData?.data?.referral_link;

  const handleStaffPageChange = (page) => {
    setStaffPage(page);
    dispatch(fetchStaffLeaderboard(page, 100, fromDate, toDate));
  };

  const handleDownloadStaff = () => {
    if (!staffLeaderboard?.items?.length) {
      toast.warning("No staff data to download");
      return;
    }
    downloadCSV(
      "staff_leaderboard",
      ["Position", "Referrer Tag", "Referrer Name", "Referral Count", "Referral Points", "Signup Only", "KYC Only", "Funded Only", "Conversion Rate"],
      staffLeaderboard.items.map((i) => [
        i.position, i.referrer_tag, i.referrer_name || "",
        i.referral_count, i.referral_points,
        i.signup_only_count ?? "", i.kyc_only_count ?? "", i.funded_only_count ?? "",
        i.conversion_rate != null ? `${i.conversion_rate.toFixed(2)}%` : "",
      ]),
    );
  };

  const handleCopyStaffLink = async (referrerTag) => {
    setCopyingTag(referrerTag);
    try {
      const response = await dispatch(fetchReferralLink(referrerTag));
      const link = response?.data?.referral_link;
      if (link) {
        await navigator.clipboard.writeText(link);
        toast.success("Referral link copied!");
      } else {
        toast.error("No referral link found");
      }
    } catch (err) {
      toast.error("Could not fetch referral link");
    } finally {
      setCopyingTag(null);
    }
  };

  const handleViewReferrer = (item) => {
    setSelectedReferrer(item);
    dispatch(fetchReferrerDetail(item.referrer_id));
    dispatch(fetchReferralLink(item.referrer_tag));
    setReferrerModal(true);
    document.body.style.overflow = "hidden";
  };

  const handleViewReferral = (item) => {
    setSelectedReferral(item);
    dispatch(fetchReferralDetail(item.referrer_id, item.referee_id));
    setReferralModal(true);
  };

  const handleCopyLink = (link) => {
    navigator.clipboard.writeText(link).then(() => {
      toast.success("Referral link copied!");
    });
  };

  const closeReferrerModal = () => {
    setReferrerModal(false);
    setSelectedReferrer(null);
    setReferralModal(false);
    setSelectedReferral(null);
    document.body.style.overflow = "auto";
  };

  const closeReferralModal = () => {
    setReferralModal(false);
    setSelectedReferral(null);
  };

  if (staffLoading) return <Loader />;
  if (staffError) return <p className="text-danger text-center">{staffError}</p>;

  return (
    <>
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: "12px" }}>
        <button onClick={handleDownloadStaff} style={downloadBtnStyle}>⬇ Download CSV</button>
      </div>
      <MDBTable responsive style={{ width: "100%", fontSize: "14px" }}>
        <MDBTableHead>
          <tr>
            <th>Position</th>
            <th>Referrer Tag</th>
            <th>Referrer Name</th>
            <th>Referral Count</th>
            <th>Referral Points</th>
            <th>Signup Only</th>
            <th>KYC Only</th>
            <th>Funded Only</th>
            <th>Conversion Rate</th>
            <th>Action</th>
          </tr>
        </MDBTableHead>
        <MDBTableBody>
          {staffLeaderboard?.items?.length > 0 ? (
            staffLeaderboard.items.map((item) => (
              <tr key={item.referrer_id}>
                <td>
                  <span style={{ fontWeight: "700", color: item.position === 1 ? "#FFD700" : item.position === 2 ? "#C0C0C0" : item.position === 3 ? "#CD7F32" : "#fff" }}>
                    #{item.position}
                  </span>
                </td>
                <td>
                  <span
                    onClick={() => copyingTag !== item.referrer_tag && handleCopyStaffLink(item.referrer_tag)}
                    style={{ color: "#4dabf7", textDecoration: "underline", fontWeight: "600", cursor: copyingTag === item.referrer_tag ? "wait" : "pointer", opacity: copyingTag === item.referrer_tag ? 0.6 : 1, display: "inline-flex", alignItems: "center", gap: "6px" }}
                    title="Click to copy referral link"
                  >
                    {item.referrer_tag}
                    {copyingTag === item.referrer_tag && (
                      <span style={{ width: "12px", height: "12px", border: "2px solid #4dabf7", borderTopColor: "transparent", borderRadius: "50%", display: "inline-block", animation: "spin 0.6s linear infinite" }} />
                    )}
                  </span>
                </td>
                <td>{item.referrer_name || "-"}</td>
                <td>{item.referral_count}</td>
                <td>{item.referral_points}</td>
                <td>{item.signup_only_count ?? "-"}</td>
                <td>{item.kyc_only_count ?? "-"}</td>
                <td>{item.funded_only_count ?? "-"}</td>
                <td>{item.conversion_rate != null ? `${item.conversion_rate.toFixed(2)}%` : "-"}</td>
                <td>
                  <p className="withdrawal-btn" onClick={() => handleViewReferrer(item)} style={{ cursor: "pointer", color: "white", width: "80px", textAlign: "center", margin: 0 }}>
                    View
                  </p>
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td colSpan="10" style={{ textAlign: "center", padding: "20px" }}>No staff leaderboard data found</td>
            </tr>
          )}
        </MDBTableBody>
      </MDBTable>

      {staffLeaderboard?.page?.total_page > 1 && (
        <div style={{ display: "flex", gap: "8px", justifyContent: "center", marginTop: "16px" }}>
          {Array.from({ length: staffLeaderboard.page.total_page }, (_, i) => i + 1).map((page) => (
            <button key={page} onClick={() => handleStaffPageChange(page)} style={{ padding: "6px 12px", border: "1px solid #444", borderRadius: "4px", background: staffPage === page ? "#007bff" : "#1a1a1a", color: "#fff", cursor: "pointer" }}>
              {page}
            </button>
          ))}
        </div>
      )}

      {/* Referrer Detail Modal */}
      {referrerModal && (
        <div style={modalOverlay} onClick={closeReferrerModal}>
          <div style={modalBox} className="referral-modal-box" onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader} className="referral-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: "18px" }}>Referrer Detail — {selectedReferrer?.referrer_tag}</h3>
                <p style={{ margin: "4px 0 0", fontSize: "13px" }}>
                  {selectedReferrer?.referrer_name && `${selectedReferrer.referrer_name} · `}
                  ID: {selectedReferrer?.referrer_id}
                </p>
              </div>
              <button style={closeBtn} onClick={closeReferrerModal}>✕</button>
            </div>
            <div style={modalBody} className="referral-modal-body">
              {detailLoading ? (
                <Loader />
              ) : detailError ? (
                <p className="text-danger text-center">{detailError}</p>
              ) : detail ? (
                <>
                  <div style={{ background: "#2a2a2a", borderRadius: "8px", padding: "14px", marginBottom: "20px", display: "flex", alignItems: "center", gap: "12px" }}>
                    <div style={{ flex: 1 }}>
                      <p style={{ fontSize: "11px", color: "#aaa", margin: "0 0 4px" }}>Referral Link</p>
                      {linkLoading ? (
                        <span style={{ fontSize: "13px", color: "#888" }}>Loading...</span>
                      ) : linkError ? (
                        <span style={{ fontSize: "13px", color: "#dc3545" }}>{linkError}</span>
                      ) : (
                        <span style={{ fontSize: "13px", color: "#fff", wordBreak: "break-all" }}>{referralLink || "-"}</span>
                      )}
                    </div>
                    {referralLink && (
                      <button onClick={() => handleCopyLink(referralLink)} style={{ padding: "6px 14px", background: "#007bff", color: "#fff", border: "none", borderRadius: "4px", cursor: "pointer", fontSize: "12px", fontWeight: "600" }}>Copy</button>
                    )}
                  </div>

                  <div style={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: "12px", marginBottom: "24px" }}>
                    {[
                      { label: "Total Referrals", value: detail.overview?.total_referrals, color: "#007bff" },
                      { label: "Completed", value: detail.overview?.completed_referrals, color: "#28a745" },
                      { label: "Pending", value: detail.overview?.pending_referrals, color: "#ffc107" },
                      { label: "Releasable Points", value: detail.overview?.releasable_referral_points, color: "#6f42c1" },
                    ].map((card) => (
                      <div key={card.label} style={{ background: "#2a2a2a", borderRadius: "8px", padding: "14px", borderLeft: `4px solid ${card.color}` }}>
                        <p style={{ fontSize: "11px", color: "#aaa", margin: 0 }}>{card.label}</p>
                        <h4 style={{ margin: "4px 0 0", color: card.color }}>{card.value ?? "-"}</h4>
                      </div>
                    ))}
                  </div>

                  <p style={{ fontWeight: "600", fontSize: "14px", marginBottom: "10px" }}>Referrals List</p>
                  <MDBTable responsive style={{ fontSize: "13px" }}>
                    <MDBTableHead>
                      <tr>
                        <th>Referee Name</th>
                        <th>Referee Tag</th>
                        <th>Points</th>
                        <th>Status</th>
                        <th>Credited</th>
                        <th>Action</th>
                      </tr>
                    </MDBTableHead>
                    <MDBTableBody>
                      {detail.referrals_list?.items?.length > 0 ? (
                        detail.referrals_list.items.map((item) => (
                          <tr key={`${item.referrer_id}-${item.referee_id}`}>
                            <td>{item.referee_name}</td>
                            <td>{item.referee_tag}</td>
                            <td>{item.referral_point}</td>
                            <td>
                              <span style={{ padding: "3px 10px", borderRadius: "12px", fontSize: "12px", fontWeight: "600", background: `${stateLabels[item.state]?.color || "#888"}33`, color: stateLabels[item.state]?.color || "#888" }}>
                                {stateLabels[item.state]?.label ?? item.state}
                              </span>
                            </td>
                            <td>
                              <span style={{ color: item.is_point_credited ? "#28a745" : "#dc3545", fontWeight: "600" }}>
                                {item.is_point_credited ? "Yes" : "No"}
                              </span>
                            </td>
                            <td>
                              <p className="withdrawal-btn" onClick={() => handleViewReferral(item)} style={{ cursor: "pointer", color: "white", width: "70px", textAlign: "center", fontSize: "12px" }}>Details</p>
                            </td>
                          </tr>
                        ))
                      ) : (
                        <tr>
                          <td colSpan="6" style={{ textAlign: "center", padding: "20px" }}>No referrals found</td>
                        </tr>
                      )}
                    </MDBTableBody>
                  </MDBTable>
                </>
              ) : null}
            </div>
          </div>
        </div>
      )}

      {/* Referral Detail Modal */}
      {referralModal && (
        <div style={{ ...modalOverlay, zIndex: 1100 }} onClick={closeReferralModal}>
          <div style={{ ...modalBox, maxWidth: "520px" }} className="referral-modal-box" onClick={(e) => e.stopPropagation()}>
            <div style={modalHeader} className="referral-modal-header">
              <div>
                <h3 style={{ margin: 0, fontSize: "18px" }}>Referral Detail</h3>
                <p style={{ margin: "4px 0 0", fontSize: "13px" }}>
                  {selectedReferral?.referrer_tag} → {selectedReferral?.referee_tag}
                </p>
              </div>
              <button style={closeBtn} onClick={closeReferralModal}>✕</button>
            </div>
            <div style={modalBody} className="referral-modal-body">
              {referralDetailLoading ? (
                <Loader />
              ) : referralDetailError ? (
                <p className="text-danger text-center">{referralDetailError}</p>
              ) : referralDetail ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "14px" }}>
                  {[
                    { label: "Referee Name", value: referralDetail.referee_name },
                    { label: "Referee Tag", value: referralDetail.referee_tag },
                    { label: "Referrer Tag", value: referralDetail.referrer_tag },
                    { label: "Referral Points", value: referralDetail.referral_point },
                    {
                      label: "Status",
                      value: (
                        <span style={{ padding: "3px 12px", borderRadius: "12px", fontSize: "12px", fontWeight: "600", background: `${stateLabels[referralDetail.state]?.color || "#888"}33`, color: stateLabels[referralDetail.state]?.color || "#888" }}>
                          {stateLabels[referralDetail.state]?.label ?? referralDetail.state}
                        </span>
                      ),
                    },
                    {
                      label: "Point Credited",
                      value: (
                        <span style={{ color: referralDetail.is_point_credited ? "#28a745" : "#dc3545", fontWeight: "600" }}>
                          {referralDetail.is_point_credited ? "Yes" : "No"}
                        </span>
                      ),
                    },
                  ].map((row) => (
                    <div key={row.label} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "12px 16px", background: "#2a2a2a", borderRadius: "8px" }}>
                      <span style={{ fontSize: "13px", color: "#aaa", fontWeight: "500" }}>{row.label}</span>
                      <span style={{ fontSize: "14px", fontWeight: "600" }}>{row.value}</span>
                    </div>
                  ))}
                </div>
              ) : null}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default StaffLeaderboard;
